import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mic, Square, Loader2, Wand2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { studio, apiError } from "@/lib/studioApi";

const EXAMPLE =
  "Remplacement d'une chaudière gaz Vaillant 24 kW chez Monsieur Dubois, avec démontage de l'ancienne chaudière, raccordement, mise en service et évacuation.";

export const DescribeQuote = ({ clients = [] }) => {
  const navigate = useNavigate();
  const [description, setDescription] = useState("");
  const [clientId, setClientId] = useState("");
  const [busy, setBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);

  const generate = async (text) => {
    const value = (text ?? description).trim();
    if (value.length < 12) return toast.error("Décrivez le chantier en une phrase complète.");
    setBusy(true);
    try {
      const { data } = await studio.post(
        "/quotes/from-description",
        { description: value, client_id: clientId },
        { timeout: 300000 },
      );
      if (data.missing?.length) sessionStorage.setItem(`studio_warnings_${data.quote_id}`, JSON.stringify(data.missing));
      toast.success(data.template ? `${data.number} créé à partir de votre modèle « ${data.template} » (${data.lines.length} lignes).` : `${data.number} créé avec ${data.lines.length} ligne(s).`);
      navigate(`/studio/devis/${data.quote_id}`);
    } catch (e) {
      toast.error(apiError(e, "Impossible de créer le devis."));
    } finally {
      setBusy(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "audio/mp4";
      const recorder = new MediaRecorder(stream, { mimeType });
      chunksRef.current = [];
      recorder.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: mimeType });
        setRecording(false);
        if (blob.size < 1200) return toast.error("Enregistrement trop court.");
        setTranscribing(true);
        const fd = new FormData();
        fd.append("file", new File([blob], `chantier.${mimeType.includes("webm") ? "webm" : "mp4"}`, { type: mimeType }));
        try {
          const { data } = await studio.post("/transcribe", fd, { timeout: 180000 });
          setDescription(data.text);
          generate(data.text);
        } catch (e) {
          toast.error(apiError(e, "Transcription impossible."));
        } finally {
          setTranscribing(false);
        }
      };
      mediaRef.current = recorder;
      recorder.start();
      setRecording(true);
    } catch {
      toast.error("Micro inaccessible. Autorisez l'accès au micro.");
    }
  };

  return (
    <div
      data-testid="describe-quote"
      className="mt-6 overflow-hidden rounded-2xl border border-[#3A5CC8]/25 dv-navy p-5 text-white shadow-[0_18px_40px_-24px_rgba(15,38,69,0.6)] sm:p-6"
    >
      <div className="flex items-center gap-2">
        <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#3A5CC8]">
          <Wand2 className="h-4 w-4" />
        </span>
        <div className="min-w-0">
          <h2 className="font-heading text-base font-bold sm:text-lg">Décrivez votre chantier</h2>
          <p className="truncate text-xs text-blue-100/80">Une phrase dictée ou écrite, et le devis est chiffré</p>
        </div>
      </div>

      <textarea
        data-testid="describe-input"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        rows={3}
        placeholder={EXAMPLE}
        className="mt-4 w-full resize-none rounded-xl border border-white/15 bg-white/10 px-3.5 py-3 text-[15px] text-white placeholder:text-blue-100/50 focus:border-white/40 focus:outline-none"
      />

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button
          type="button"
          data-testid="describe-mic"
          disabled={busy || transcribing}
          onClick={recording ? () => mediaRef.current?.stop() : startRecording}
          className={`inline-flex min-h-[48px] items-center gap-2 rounded-xl px-4 text-sm font-bold transition-colors ${
            recording ? "bg-rose-600 text-white" : "border border-white/25 text-white hover:bg-white/10"
          } disabled:opacity-40`}
        >
          {transcribing ? <Loader2 className="h-4 w-4 animate-spin" /> : recording ? <Square className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
          {recording ? "Terminer" : "Dicter"}
        </button>
        <select
          data-testid="describe-client"
          value={clientId}
          onChange={(e) => setClientId(e.target.value)}
          className="min-h-[48px] flex-1 rounded-xl border border-white/15 bg-white/10 px-3 text-sm text-white focus:outline-none sm:max-w-[240px]"
        >
          <option value="" className="text-[#0F2645]">Client détecté dans la phrase</option>
          {clients.map((c) => (
            <option key={c.id} value={c.id} className="text-[#0F2645]">{c.company || c.name}</option>
          ))}
        </select>
        <button
          type="button"
          data-testid="describe-submit"
          disabled={busy || description.trim().length < 12}
          onClick={() => generate()}
          className="inline-flex min-h-[48px] flex-1 items-center justify-center gap-2 rounded-xl bg-white px-5 text-sm font-bold text-[#0F2645] transition-transform active:scale-95 disabled:opacity-40 sm:flex-none"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
          {busy ? "Chiffrage en cours…" : "Chiffrer le devis"}
        </button>
      </div>
      <p className="mt-2.5 text-[11px] leading-relaxed text-blue-100/60">
        L'assistant applique vos prix, propose les postes manquants et signale ce qu'il reste à confirmer avec le
        client. Rien n'est envoyé avant votre validation.
      </p>
    </div>
  );
};
